import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Download, Refresh, EditOutlined, InfoOutlined } from "@mui/icons-material";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { getStockReconciliation, updateUnlistedInventory } from "../../redux/admin/adminSlice";

interface StockReconRow {
  stockId: number;
  stockName: string;
  holdingAsPerDp: number;
  deliveryReceivable: number;
  deliveryPendingSale: number;
  paymentPendingPurchase: number;
  fundReceivableSale: number;
  remarks: string | null;
}

const StockReconciliation: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [data, setData] = useState<StockReconRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Modal state
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<StockReconRow | null>(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(getStockReconciliation()).unwrap();
      if (response && response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching stock reconciliation:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportToExcel = () => {
    const exportData = data.map((row) => {
      const total = (Number(row.holdingAsPerDp) || 0) + (Number(row.deliveryReceivable) || 0);
      const netPosition = total - (Number(row.deliveryPendingSale) || 0);
      
      return {
        "Security Name": row.stockName,
        "Holding as per DP (A)": Number(row.holdingAsPerDp) || 0,
        "Delivery Receivable (Purchase) (B)": Number(row.deliveryReceivable) || 0,
        "Total (D = A + B)": total,
        "Delivery Pending (Sale) (E)": Number(row.deliveryPendingSale) || 0,
        "Net Position (F = D - E)": netPosition,
        "Fund Receivable (Sale) (₹) (I)": Number(row.fundReceivableSale) || 0,
        "Payment Pending (Purchase) (₹) (J)": Number(row.paymentPendingPurchase) || 0,
        "Remarks": row.remarks || "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Reconciliation");
    XLSX.writeFile(workbook, `Stock_Reconciliation_${new Date().getTime()}.xlsx`);
  };

  const handleOpenModal = (row: StockReconRow) => {
    setSelectedStock(row);
    setRemarkInput(row.remarks || "");
    setRemarkModalOpen(true);
  };

  const handleSaveRemark = async () => {
    if (!selectedStock) return;
    setSaving(true);
    try {
      const res = await dispatch(
        updateUnlistedInventory({
          inventoryId: selectedStock.stockId,
          remarks: remarkInput,
        })
      ).unwrap();
      
      if (res.statusCode === 200) {
        toast.success("Remark updated successfully");
        setRemarkModalOpen(false);
        fetchData();
      } else {
        toast.error(res.message || "Failed to update remark");
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred while saving");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-lightBg py-8 px-4 sm:px-6 lg:px-8"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "20px" }}
    >
      <Box sx={{ width: "98%" }}>
        {/* Header Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              DP Reconciliation
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
              sx={{
                borderWidth: 2,
                "&:hover": { borderWidth: 2 },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Download />}
              onClick={exportToExcel}
              disabled={loading || data.length === 0}
              sx={{ fontWeight: 600, px: 3 }}
            >
              Export to Excel
            </Button>
          </Box>
        </Box>

        {/* Main Table Paper */}
        <Paper elevation={3} sx={{ borderRadius: "12px", overflow: "hidden", mb: 4 }}>
          <TableContainer sx={{ maxHeight: "calc(100vh - 220px)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Security Name
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Holding as per DP (A)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Delivery Receivable (B)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Total (D = A+B)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Delivery Pending Sale (E)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Net Position (F = D-E)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Fund Receivable (I)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Payment Pending (J)
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 15, color: "white", backgroundColor: "primary.main", py: 2, whiteSpace: "nowrap" }}>
                    Remarks
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={40} />
                      <Typography mt={2} color="textSecondary">
                        Loading reconciliation data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Typography color="textSecondary" fontSize={16}>
                        No stock reconciliation data available.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => {
                    const holdingA = Number(row.holdingAsPerDp) || 0;
                    const deliveryB = Number(row.deliveryReceivable) || 0;
                    const totalD = holdingA + deliveryB;
                    const deliveryE = Number(row.deliveryPendingSale) || 0;
                    const netPositionF = totalD - deliveryE;
                    
                    const isNegative = netPositionF < 0;

                    return (
                      <TableRow
                        key={row.stockId}
                        sx={{
                          backgroundColor: isNegative ? "rgba(211,47,47,0.06)" : "inherit",
                          "&:hover": {
                            backgroundColor: isNegative ? "rgba(211,47,47,0.12)" : "#f0f4ff",
                          },
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Typography fontSize={15} fontWeight={isNegative ? 700 : 500} color="text.primary">
                            {row.stockName}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography fontSize={15} color="text.primary">
                            {holdingA.toLocaleString()}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography fontSize={15} color="text.primary">
                            {deliveryB.toLocaleString()}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography fontSize={15} fontWeight={700} color="text.primary">
                            {totalD.toLocaleString()}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography fontSize={15} color="text.primary">
                            {deliveryE.toLocaleString()}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography
                            fontSize={15}
                            fontWeight={700}
                            color={isNegative ? "error.main" : "success.main"}
                          >
                            {netPositionF.toLocaleString()}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography fontSize={15} color="text.primary">
                            ₹ {Number(row.fundReceivableSale || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography fontSize={15} color="text.primary">
                            ₹ {Number(row.paymentPendingPurchase || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                            {row.remarks && row.remarks.trim() !== "" && (
                              <Tooltip title={row.remarks} arrow placement="top">
                                <IconButton size="small" color="info">
                                  <InfoOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={row.remarks ? "Edit Remark" : "Add Remark"} arrow placement="top">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleOpenModal(row)}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Edit Remark Modal */}
      <Dialog
        open={remarkModalOpen}
        onClose={() => !saving && setRemarkModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedStock?.remarks ? "Edit Remark" : "Add Remark"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Security: <strong>{selectedStock?.stockName}</strong>
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Remarks"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={remarkInput}
            onChange={(e) => setRemarkInput(e.target.value)}
            disabled={saving}
            placeholder="Enter any notes or status updates here..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button 
            onClick={() => setRemarkModalOpen(false)} 
            disabled={saving}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveRemark} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : "Save Remark"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StockReconciliation;
